/**
 * The form settings that survive a visit.
 *
 * The line is drawn at: **how the video looks and sounds** is a preference and
 * comes back; **what this particular video is about** does not. Nobody makes
 * the same comparison twice, but everybody wants their colour, their voice and
 * their watermark already in place — so the topic, the two names and the two
 * option images are deliberately not stored.
 *
 * Everything read back is validated against the live option lists, because a
 * stored value outlives the build that wrote it. A character or a voice can be
 * removed while a preference still names it, and an unvalidated `voiceName`
 * would reach the Live API as a `speechConfig` naming a voice that no longer
 * exists. Anything that fails validation falls back to the current default, so
 * the defaults stay exactly what they are for a first-time visitor.
 */
import { CHARACTERS } from "./characters";
import { DURATIONS, WATERMARK_CORNERS } from "./form-options";
import { pickedImageFromFile } from "./image-picker";
import { ASPECTS } from "./layout";
import type { AspectId, ComparisonSpec, PickedImage, WatermarkCorner } from "./types";
import { LANGUAGES, VOICES } from "./voices";

const STORAGE_KEY = "screenlet:ai-video-prefs";
/** Its own key: a watermark too big for the quota must not take the rest with it. */
const WATERMARK_KEY = "screenlet:ai-video-watermark";

/**
 * A data URL is base64 (+1/3) held as UTF-16 (×2), so a file this size costs
 * roughly 1.4MB of a ~5MB budget. Watermarks are small marks; anything larger
 * is a background someone dropped in the wrong slot, and is not worth the
 * entire allowance.
 */
const MAX_WATERMARK_BYTES = 512 * 1024;

/** Which parts of the spec are settings rather than content. */
export type StoredPreferences = Pick<
	ComparisonSpec,
	| "characterId"
	| "themeColor"
	| "backgroundColor"
	| "aspect"
	| "languageLabel"
	| "voiceName"
	| "targetSeconds"
	| "showCaptions"
	| "watermarkText"
	| "watermarkCorner"
>;

export function readPreferences({
	defaults,
}: {
	defaults: ComparisonSpec;
}): StoredPreferences {
	const stored = readJson();

	return {
		characterId:
			oneOf({ value: stored.characterId, allowed: CHARACTERS.map((c) => c.id) }) ??
			defaults.characterId,
		themeColor: hexColor({ value: stored.themeColor }) ?? defaults.themeColor,
		backgroundColor:
			hexColor({ value: stored.backgroundColor }) ?? defaults.backgroundColor,
		aspect:
			(oneOf({
				value: stored.aspect,
				allowed: ASPECTS.map((a) => a.id),
			}) as AspectId | null) ?? defaults.aspect,
		languageLabel:
			oneOf({
				value: stored.languageLabel,
				allowed: LANGUAGES.map((l) => l.label),
			}) ?? defaults.languageLabel,
		voiceName:
			oneOf({ value: stored.voiceName, allowed: VOICES.map((v) => v.name) }) ??
			defaults.voiceName,
		targetSeconds:
			typeof stored.targetSeconds === "number" &&
			DURATIONS.some((d) => d.seconds === stored.targetSeconds)
				? stored.targetSeconds
				: defaults.targetSeconds,
		showCaptions:
			typeof stored.showCaptions === "boolean"
				? stored.showCaptions
				: defaults.showCaptions,
		watermarkText:
			typeof stored.watermarkText === "string"
				? stored.watermarkText
				: defaults.watermarkText,
		watermarkCorner:
			(oneOf({
				value: stored.watermarkCorner,
				allowed: WATERMARK_CORNERS.map((c) => c.value),
			}) as WatermarkCorner | null) ?? defaults.watermarkCorner,
	};
}

export function storePreferences({ spec }: { spec: ComparisonSpec }): void {
	const preferences: StoredPreferences = {
		characterId: spec.characterId,
		themeColor: spec.themeColor,
		backgroundColor: spec.backgroundColor,
		aspect: spec.aspect,
		languageLabel: spec.languageLabel,
		voiceName: spec.voiceName,
		targetSeconds: spec.targetSeconds,
		showCaptions: spec.showCaptions,
		watermarkText: spec.watermarkText,
		watermarkCorner: spec.watermarkCorner,
	};

	try {
		window.localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
	} catch {
		/* private mode, or the quota is full — settings just won't persist */
	}
}

/**
 * The watermark image is the one asset worth keeping: it is the same mark every
 * time, and re-picking it is the most tedious part of setting the page up
 * again. It is stored as a data URL rather than an object URL, which does not
 * outlive the tab that made it.
 */
export async function storeWatermarkImage({
	image,
}: {
	image: PickedImage | null;
}): Promise<void> {
	if (!image || image.file.size > MAX_WATERMARK_BYTES) {
		remove({ key: WATERMARK_KEY });
		return;
	}

	try {
		window.localStorage.setItem(
			WATERMARK_KEY,
			await toDataUrl({ file: image.file }),
		);
	} catch {
		// Over quota. Drop it rather than leave a half-written value behind.
		remove({ key: WATERMARK_KEY });
	}
}

export async function readWatermarkImage(): Promise<PickedImage | null> {
	const dataUrl = readString({ key: WATERMARK_KEY });
	if (!dataUrl) return null;

	try {
		const blob = await (await fetch(dataUrl)).blob();
		const file = new File([blob], "watermark", { type: blob.type });
		return await pickedImageFromFile({ file });
	} catch {
		// Corrupt or no longer decodable: forget it instead of failing the page.
		remove({ key: WATERMARK_KEY });
		return null;
	}
}

function readJson(): Partial<Record<keyof StoredPreferences, unknown>> {
	const raw = readString({ key: STORAGE_KEY });
	if (!raw) return {};
	try {
		const parsed: unknown = JSON.parse(raw);
		return parsed && typeof parsed === "object"
			? (parsed as Record<string, unknown>)
			: {};
	} catch {
		return {};
	}
}

function readString({ key }: { key: string }): string | null {
	if (typeof window === "undefined") return null;
	try {
		return window.localStorage.getItem(key);
	} catch {
		return null;
	}
}

function remove({ key }: { key: string }): void {
	try {
		window.localStorage.removeItem(key);
	} catch {
		/* nothing stored anyway */
	}
}

function oneOf({
	value,
	allowed,
}: {
	value: unknown;
	allowed: string[];
}): string | null {
	return typeof value === "string" && allowed.includes(value) ? value : null;
}

function hexColor({ value }: { value: unknown }): string | null {
	return typeof value === "string" && /^#[0-9a-f]{6}$/i.test(value)
		? value
		: null;
}

function toDataUrl({ file }: { file: File }): Promise<string> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => resolve(String(reader.result));
		reader.onerror = () => reject(new Error("Couldn't read that image."));
		reader.readAsDataURL(file);
	});
}
